import { knobsOf, presetProfileKnobs } from '../botProfile/document.ts';
import { coerceBotKnobs } from '../botProfile/migrate.ts';
import type { BotProfileKnobs } from '../botProfile/types.ts';
import { BOT_LEVEL_KNOBS, type BotLevelKnobs } from './levels.ts';

/** Levels a Hot Seat seat can pick. */
export const LIVE_BOT_LEVELS = ['easy', 'medium', 'hard', 'expert'] as const;
export type LiveBotLevel = (typeof LIVE_BOT_LEVELS)[number];

/** A seat sailed by a saved bot profile (`bots/<id>.json`), carried by value for the race. */
export type ProfileSeat = {
  bot: 'profile';
  /** Catalog id (the file stem). */
  id: string;
  name: string;
  knobs: BotProfileKnobs;
};

/**
 * Who clicks for a seat. Session only: not persisted, so no `version`. A profile seat carries its
 * knobs, not just the id, so Race again and Restart sail the same bot even if the file changes.
 */
export type SeatController = 'human' | { bot: LiveBotLevel } | ProfileSeat;

export const HUMAN_SEAT: SeatController = 'human';

export function isLiveBotLevel(value: unknown): value is LiveBotLevel {
  return typeof value === 'string' && (LIVE_BOT_LEVELS as readonly string[]).includes(value);
}

export function isProfileSeat(controller: SeatController | undefined): controller is ProfileSeat {
  return controller !== undefined && controller !== 'human' && controller.bot === 'profile';
}

/** A built-in level, or null for a human or a profile seat. */
export function seatBotLevel(controller: SeatController | undefined): LiveBotLevel | null {
  return controller !== undefined && controller !== 'human' && controller.bot !== 'profile'
    ? controller.bot
    : null;
}

/** Any bot: a built-in level or a saved profile. */
export function seatIsBot(controller: SeatController | undefined): boolean {
  return controller !== undefined && controller !== 'human';
}

/** The planner knobs a bot seat races with, or null for a human. */
export function seatBotKnobs(controller: SeatController | undefined): BotLevelKnobs | null {
  if (isProfileSeat(controller)) {
    return knobsOf(controller.knobs);
  }

  const level = seatBotLevel(controller);
  return level ? BOT_LEVEL_KNOBS[level] : null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** One controller per seat. Anything missing or unknown is a human, so a bad handoff never seats a bot. */
export function resolveSeatControllers(raw: unknown, count: number): SeatController[] {
  const list = Array.isArray(raw) ? raw : [];

  return Array.from({ length: count }, (_, index) => {
    const entry: unknown = list[index];

    if (!isRecord(entry)) {
      return HUMAN_SEAT;
    }

    if (isLiveBotLevel(entry.bot)) {
      return { bot: entry.bot };
    }

    if (entry.bot === 'profile' && typeof entry.id === 'string' && isRecord(entry.knobs)) {
      return {
        bot: 'profile',
        id: entry.id,
        name: typeof entry.name === 'string' ? entry.name : entry.id,
        knobs: coerceBotKnobs(entry.knobs, presetProfileKnobs('expert')),
      };
    }

    return HUMAN_SEAT;
  });
}
